import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { mapSaintToFormInitial } from "../lib/saints/formMapping";
import SaintForm from "../components/SaintForm";
import Toast from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { getSaint, saveSaint, renameSaint } from "../services/saintsRepo";

export default function AdminEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [initial, setInitial] = useState(undefined); // undefined=loading, null=not found
  const { message: toast, show: showToast, clear: clearToast } = useToast(2500);

  useEffect(() => {
    if (initial === undefined) {
      document.title = "Dulia: Edit";
      return;
    }
    if (initial === null) {
      document.title = "Dulia: Not found";
      return;
    }
    const name = initial?.name ? String(initial.name).trim() : "";
    document.title = name ? `Dulia: ${name}` : "Dulia: Edit";
  }, [initial]);

  useEffect(() => {
    return () => {
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
    };
  }, []);

  const navTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!id) return;

    (async () => {
      const saint = await getSaint(id);
      if (!saint) return setInitial(null);

      // ensure the form has access to current doc id for the slug editor
      const mapped = mapSaintToFormInitial(saint);
      setInitial({ ...mapped, id });
    })();
  }, [id]);

  async function handleSave(payload, meta) {
    if (!id) return { ok: false, message: "Missing id." };

    const desiredId = String(meta?.docId || id).trim();

    // if docId changed: rename
    if (desiredId && desiredId !== id) {
      await renameSaint(id, desiredId, payload, { existingRand: initial?.rand });

      showToast(`Renamed: saints/${id} → saints/${desiredId}`);

      if (navTimerRef.current) clearTimeout(navTimerRef.current);
      navTimerRef.current = setTimeout(() => {
        navigate(`/profile/${desiredId}`);
      }, 650);

      return { ok: true, message: `Renamed: saints/${desiredId}` };
    }

    // normal save
    await saveSaint(id, payload, { existingRand: initial?.rand });

    showToast(`Saved: saints/${id}`);

    if (navTimerRef.current) clearTimeout(navTimerRef.current);
    navTimerRef.current = setTimeout(() => {
      navigate(`/profile/${id}`);
    }, 650);

    return { ok: true, message: `Saved: saints/${id}` };
  }

  if (initial === undefined) return <div>Loading…</div>;

  if (initial === null) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-bold">Not found</h1>
        <Link className="underline" to="/search">
          Back to search
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Toast message={toast} onClose={clearToast} />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Edit {initial.name}</h1>
        <Link className="rounded border px-3 py-2 text-sm" to={`/profile/${id}`}>
          View Profile
        </Link>
      </div>

      <SaintForm
        mode="edit"
        initialValues={initial}
        onSubmit={handleSave}
        submitLabel="Save"
      />
    </div>
  );
}
